// Renderer for handling all canvas drawing operations
export class Renderer {
  private ctx: CanvasRenderingContext2D;
  public width: number;
  public height: number;
  private screenShake: number = 0;
  private screenShakeTime: number = 0;
  private screenShakeDuration: number = 0;
  private screenShakeAmplitude: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get 2D context from canvas');
    }
    this.ctx = context;
    this.width = canvas.width;
    this.height = canvas.height;
  }

  public addScreenShake(type: 'light' | 'medium' | 'heavy'): void {
    // Non-stacking: new shake overrides previous
    switch (type) {
      case 'light':
        this.screenShakeAmplitude = 2; // 1-2 pixels
        this.screenShakeDuration = 70; // 60-80ms
        break;
      case 'medium':
        this.screenShakeAmplitude = 4; // 3-4 pixels
        this.screenShakeDuration = 110; // 90-120ms
        break;
      case 'heavy':
        this.screenShakeAmplitude = 6; // 5-6 pixels (MAX)
        this.screenShakeDuration = 140; // 120-150ms
        break;
    }
    this.screenShakeTime = this.screenShakeDuration;
  }

  public updateScreenShake(deltaTime: number): void {
    if (this.screenShakeTime > 0) {
      this.screenShakeTime -= deltaTime * 1000; // Convert to milliseconds
      this.screenShakeTime = Math.max(0, this.screenShakeTime);
    }
  }

  public applyScreenShake(): void {
    if (this.screenShakeTime > 0) {
      // Deterministic sine wave decay (fast decay)
      const progress = 1 - (this.screenShakeTime / this.screenShakeDuration);
      const easedProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic
      const currentAmplitude = this.screenShakeAmplitude * (1 - easedProgress);
      
      // Sine wave oscillation
      const shakeX = Math.sin(progress * Math.PI * 8) * currentAmplitude;
      const shakeY = Math.sin(progress * Math.PI * 6 + Math.PI/4) * currentAmplitude;
      
      this.ctx.translate(shakeX, shakeY);
    }
  }

  public clear(color: string = '#000000'): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  public drawRect(x: number, y: number, width: number, height: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x, y, width, height);
  }

  public drawCircle(x: number, y: number, radius: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  public drawText(text: string, x: number, y: number, color: string = '#ffffff', font: string = '16px "Courier New"'): void {
    this.ctx.fillStyle = color;
    this.ctx.font = font;
    this.ctx.fillText(text, x, y);
  }

  public drawTextCentered(text: string, y: number, color: string = '#ffffff', font: string = '16px "Courier New"'): void {
    this.ctx.fillStyle = color;
    this.ctx.font = font;
    const metrics = this.ctx.measureText(text);
    const x = (this.width - metrics.width) / 2;
    this.ctx.fillText(text, x, y);
  }

  public drawLine(x1: number, y1: number, x2: number, y2: number, color: string, width: number = 1): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }

  public drawImage(image: HTMLImageElement, x: number, y: number, width?: number, height?: number): void {
    if (width && height) {
      this.ctx.drawImage(image, x, y, width, height);
    } else {
      this.ctx.drawImage(image, x, y);
    }
  }

  public getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  public setAlpha(alpha: number): void {
    this.ctx.globalAlpha = alpha;
  }

  public resetAlpha(): void {
    this.ctx.globalAlpha = 1.0;
  }
}
